"use client";

import * as React from "react";
import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DatePickerWithRangeProps {
  from?: Date;
  to?: Date;
  onUpdate?: (range: { from: Date; to: Date }) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function DatePickerWithRange({
  from,
  to,
  onUpdate,
  className,
  placeholder = "Selecione o período",
  disabled = false,
}: DatePickerWithRangeProps) {
  const [date, setDate] = React.useState<DateRange | undefined>({
    from: from || addDays(new Date(), -30),
    to: to || new Date(),
  });

  // Mantém o estado interno em sincronia quando o pai controla o range.
  React.useEffect(() => {
    if (from || to) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDate((prev) => ({
        from: from ?? prev?.from,
        to: to ?? prev?.to,
      }));
    }
  }, [from, to]);

  const handleSelect = React.useCallback(
    (newDate: DateRange | undefined) => {
      setDate(newDate);
      if (newDate?.from && newDate?.to && onUpdate) {
        onUpdate({ from: newDate.from, to: newDate.to });
      }
    },
    [onUpdate],
  );

  const setPreset = React.useCallback(
    (preset: string) => {
      const now = new Date();
      const today = startOfDay(now);

      const weekStartsOn = 1 as const; // pt-BR: semana inicia na segunda

      const presets: Record<string, { from: Date; to: Date }> = {
        hoje: { from: today, to: today },
        ontem: { from: subDays(today, 1), to: subDays(today, 1) },
        // Requisito: últimos 7 dias NÃO conta o dia atual (D-7 até D-1)
        ultimos7: { from: subDays(today, 7), to: subDays(today, 1) },
        estaSemana: { from: startOfWeek(today, { weekStartsOn }), to: today },
        semanaPassada: {
          from: startOfWeek(subWeeks(today, 1), { weekStartsOn }),
          to: endOfWeek(subWeeks(today, 1), { weekStartsOn }),
        },
        esteMes: { from: startOfMonth(today), to: today },
        mesPassado: {
          from: startOfMonth(subMonths(today, 1)),
          to: endOfMonth(subMonths(today, 1)),
        },
        ultimos14: { from: subDays(today, 13), to: today },
        ultimos30: { from: subDays(today, 29), to: today },
        ultimos90: { from: subDays(today, 89), to: today },
        ultimos12Meses: { from: subMonths(today, 12), to: today },
        janeiroAteHoje: { from: startOfYear(today), to: today },
      };

      const range = presets[preset];
      if (!range) return;

      // Normaliza para start/end of day para evitar ranges “quebrados” por horário.
      handleSelect({
        from: startOfDay(range.from),
        to: endOfDay(range.to),
      });
    },
    [handleSelect],
  );

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-[300px] justify-start text-left font-normal",
              !date && "text-muted-foreground",
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "dd/MM/yyyy", { locale: ptBR })} -{" "}
                  {format(date.to, "dd/MM/yyyy", { locale: ptBR })}
                </>
              ) : (
                format(date.from, "dd/MM/yyyy", { locale: ptBR })
              )
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex">
            <div className="flex w-56 flex-col gap-1 border-r border-border/60 p-2">
              <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                Rápido
              </div>

              <Button
                type="button"
                variant="ghost"
                className="h-8 justify-start px-2 text-sm"
                onClick={() => setPreset("hoje")}
              >
                Hoje
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-8 justify-start px-2 text-sm"
                onClick={() => setPreset("ontem")}
              >
                Ontem
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-8 justify-start px-2 text-sm"
                onClick={() => setPreset("estaSemana")}
              >
                Esta semana
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-8 justify-start px-2 text-sm"
                onClick={() => setPreset("semanaPassada")}
              >
                Semana passada
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-8 justify-start px-2 text-sm"
                onClick={() => setPreset("mesPassado")}
              >
                Mês passado
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-8 justify-start px-2 text-sm"
                onClick={() => setPreset("esteMes")}
              >
                Este mês
              </Button>

              <div className="my-1 border-t border-border/60" />

              <Button
                type="button"
                variant="ghost"
                className="h-8 justify-start px-2 text-sm"
                onClick={() => setPreset("ultimos7")}
              >
                Últimos 7 dias
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-8 justify-start px-2 text-sm"
                onClick={() => setPreset("ultimos14")}
              >
                Últimos 14 dias
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-8 justify-start px-2 text-sm"
                onClick={() => setPreset("ultimos30")}
              >
                Últimos 30 dias
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-8 justify-start px-2 text-sm"
                onClick={() => setPreset("ultimos90")}
              >
                Últimos 90 dias
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-8 justify-start px-2 text-sm"
                onClick={() => setPreset("ultimos12Meses")}
              >
                Últimos 12 meses
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-8 justify-start px-2 text-sm"
                onClick={() => setPreset("janeiroAteHoje")}
              >
                Janeiro até hoje
              </Button>
            </div>

            <Calendar
              initialFocus
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              onSelect={handleSelect}
              numberOfMonths={2}
              locale={ptBR}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Componente de data simples para casos onde só precisa de uma data
interface DatePickerProps {
  date?: Date;
  onSelect?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

function DatePicker({
  date,
  onSelect,
  placeholder = "Selecione uma data",
  disabled = false,
  className,
}: DatePickerProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "w-[280px] justify-start text-left font-normal",
            !date && "text-muted-foreground",
            className,
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? (
            format(date, "dd/MM/yyyy", { locale: ptBR })
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={date}
          onSelect={onSelect}
          initialFocus
          locale={ptBR}
        />
      </PopoverContent>
    </Popover>
  );
}
